import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { verificationService } from '../../../services/verificationService';
import { useAuth } from '../../../contexts/AuthContext';
import VerificationRequestCard from './VerificationRequestCard';
import VerificationDetailModal from './VerificationDetailModal';

const VerificationDashboard = () => {
  const { user, userProfile } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    underReview: 0,
    approved: 0,
    rejected: 0,
    candidates: 0,
    employers: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [realtimeChannel, setRealtimeChannel] = useState(null);

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'candidate', label: 'Candidates' },
    { value: 'employer', label: 'Employers' }
  ];

  // Load initial data
  useEffect(() => {
    loadVerificationData();
  }, []);

  // Setup real-time subscription
  useEffect(() => {
    const channel = verificationService?.subscribeToRequests((payload) => {
      if (payload?.eventType === 'INSERT') {
        setRequests(prev => [transformRequest(payload?.new), ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setRequests(prev => prev?.map(req => 
          req?.id === payload?.new?.id ? transformRequest(payload?.new) : req
        ));
      } else if (payload?.eventType === 'DELETE') {
        setRequests(prev => prev?.filter(req => req?.id !== payload?.old?.id));
      }
    });

    setRealtimeChannel(channel);

    return () => {
      if (channel) {
        verificationService?.unsubscribe(channel);
      }
    };
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = requests;

    if (statusFilter !== 'all') {
      filtered = filtered?.filter(req => req?.status === statusFilter);
    }

    if (roleFilter !== 'all') {
      filtered = filtered?.filter(req => req?.role === roleFilter);
    }

    setFilteredRequests(filtered);
  }, [requests, statusFilter, roleFilter]);

  const transformRequest = (dbRow) => {
    return {
      id: dbRow?.id,
      fullName: dbRow?.full_name,
      email: dbRow?.email,
      role: dbRow?.role,
      status: dbRow?.status,
      bio: dbRow?.bio,
      experience: dbRow?.experience,
      industry: dbRow?.industry,
      seniority: dbRow?.seniority,
      location: dbRow?.location,
      companyName: dbRow?.company_name,
      companyWebsite: dbRow?.company_website,
      linkedinUrl: dbRow?.linkedin_url,
      qualificationScore: dbRow?.qualification_score,
      verificationScore: dbRow?.verification_score,
      riskLevel: dbRow?.risk_level,
      submittedAt: dbRow?.submitted_at,
      reviewedAt: dbRow?.reviewed_at,
      lastStatusChange: dbRow?.last_status_change
    };
  };

  const loadVerificationData = async () => {
    try {
      setLoading(true);
      const [requestsData, statsData] = await Promise.all([
        verificationService?.getAllRequests(),
        verificationService?.getVerificationStats()
      ]);

      setRequests(requestsData);
      setStats(statsData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request) => {
    try {
      await verificationService?.approveRequest(
        request?.id,
        'Approved by admin',
        user?.id,
        userProfile?.fullName || 'Admin'
      );
    } catch (err) {
      console.error('Error approving request:', err);
    }
  };

  const handleReject = async (request, reason) => {
    try {
      await verificationService?.rejectRequest(
        request?.id,
        reason,
        'Rejected by admin',
        user?.id
      );
    } catch (err) {
      console.error('Error rejecting request:', err);
    }
  };

  const handleMarkUnderReview = async (request) => {
    try {
      await verificationService?.markUnderReview(
        request?.id,
        user?.id,
        userProfile?.fullName || 'Admin'
      );
    } catch (err) {
      console.error('Error marking under review:', err);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailModal(true);
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      pending: 'bg-warning/10 text-warning',
      under_review: 'bg-primary/10 text-primary',
      approved: 'bg-success/10 text-success',
      rejected: 'bg-error/10 text-error'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader" size={48} className="animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error/10 border border-error/20 rounded-2xl p-6 text-center">
        <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-error" />
        <p className="text-error font-medium">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-2xl p-6 elevation-sm border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Clock" size={24} className="text-warning" />
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor('pending')}`}>
              Pending
            </span>
          </div>
          <p className="text-3xl font-bold text-foreground">{stats?.pending}</p>
          <p className="text-sm text-muted-foreground mt-1">Awaiting Review</p>
        </div>

        <div className="bg-card rounded-2xl p-6 elevation-sm border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Eye" size={24} className="text-primary" />
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor('under_review')}`}>
              In Review
            </span>
          </div>
          <p className="text-3xl font-bold text-foreground">{stats?.underReview}</p>
          <p className="text-sm text-muted-foreground mt-1">Under Review</p>
        </div>

        <div className="bg-card rounded-2xl p-6 elevation-sm border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="CheckCircle" size={24} className="text-success" />
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor('approved')}`}>
              Approved
            </span>
          </div>
          <p className="text-3xl font-bold text-foreground">{stats?.approved}</p>
          <p className="text-sm text-muted-foreground mt-1">Total Approved</p>
        </div>

        <div className="bg-card rounded-2xl p-6 elevation-sm border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="XCircle" size={24} className="text-error" />
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor('rejected')}`}>
              Rejected
            </span>
          </div>
          <p className="text-3xl font-bold text-foreground">{stats?.rejected}</p>
          <p className="text-sm text-muted-foreground mt-1">Total Rejected</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-2xl p-6 elevation-sm border border-border">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="Filter" size={20} className="text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground">Filter Requests</h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filter by status"
              className="w-full sm:w-48"
            />
            <Select
              options={roleOptions}
              value={roleFilter}
              onChange={setRoleFilter}
              placeholder="Filter by role"
              className="w-full sm:w-48"
            />
            <Button
              variant="outline"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={loadVerificationData}
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {filteredRequests?.length === 0 ? (
          <div className="bg-card rounded-2xl p-12 text-center elevation-sm border border-border">
            <Icon name="Inbox" size={64} className="mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Requests Found</h3>
            <p className="text-muted-foreground">
              {statusFilter !== 'all' || roleFilter !== 'all' ?'Try adjusting your filters' :'No verification requests available'}
            </p>
          </div>
        ) : (
          filteredRequests?.map((request) => (
            <VerificationRequestCard
              key={request?.id}
              request={request}
              onApprove={handleApprove}
              onReject={handleReject}
              onMarkUnderReview={handleMarkUnderReview}
              onViewDetails={handleViewDetails}
            />
          ))
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedRequest && (
        <VerificationDetailModal
          request={selectedRequest}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedRequest(null);
          }}
          onApprove={handleApprove}
          onReject={handleReject}
          onMarkUnderReview={handleMarkUnderReview}
        />
      )}
    </div>
  );
};

export default VerificationDashboard;